import {useMutation, useQueryClient} from '@github-ui/react-query'
import {fetchCAPI} from '../utils/fetch-capi'
import {taskArchiveApiPath} from '../utils/api-url-helpers'
import {agentTaskQueryKey} from './use-task'
import {agentTasksQueryKey} from './useTasks'
import {useArchiveDialogOptional, ARCHIVE_STATUS} from '../contexts/ArchiveDialogContext'

interface UseArchiveTaskArgs {
  taskId?: string
  repoOwner?: string
  repoName?: string
}

export function useArchiveTask({taskId, repoOwner, repoName}: UseArchiveTaskArgs) {
  const queryClient = useQueryClient()
  const archiveDialog = useArchiveDialogOptional()

  return useMutation({
    mutationKey: ['archiveTask', repoOwner, repoName, taskId],
    mutationFn: async () => {
      if (!taskId) {
        throw new Error('taskId is required')
      }

      const path = taskArchiveApiPath(taskId, repoOwner, repoName)
      const response = await fetchCAPI({
        path,
        method: 'POST',
      })
      return response
    },
    onMutate: async () => {
      // Cancel in-flight queries to avoid overwriting optimistic update
      await queryClient.cancelQueries({queryKey: agentTasksQueryKey})
      if (repoOwner && repoName) {
        await queryClient.cancelQueries({queryKey: ['repo-tasks', repoOwner, repoName]})
      }
      const taskDetailKey = agentTaskQueryKey(repoOwner, repoName, taskId)
      await queryClient.cancelQueries({queryKey: taskDetailKey})

      // Snapshot all matching query caches for rollback
      const previousQueries = queryClient.getQueriesData<unknown[]>({queryKey: agentTasksQueryKey})
      const previousRepoQueries =
        repoOwner && repoName
          ? queryClient.getQueriesData<unknown[]>({queryKey: ['repo-tasks', repoOwner, repoName]})
          : []
      const previousTaskDetail = queryClient.getQueryData<Record<string, unknown>>(taskDetailKey)

      // Optimistically remove the task from all matching list caches
      queryClient.setQueriesData<[Array<{id?: string}>, ...unknown[]]>({queryKey: agentTasksQueryKey}, oldData => {
        if (!oldData) return oldData
        const [tasks, ...rest] = oldData
        return [tasks.filter(t => t.id !== taskId), ...rest] as typeof oldData
      })

      if (repoOwner && repoName) {
        queryClient.setQueriesData<[Array<{id?: string}>, ...unknown[]]>(
          {queryKey: ['repo-tasks', repoOwner, repoName]},
          oldData => {
            if (!oldData) return oldData
            const [tasks, ...rest] = oldData
            return [tasks.filter(t => t.id !== taskId), ...rest] as typeof oldData
          },
        )
      }

      // Optimistically update the task detail to show archived status
      if (previousTaskDetail) {
        queryClient.setQueryData(taskDetailKey, {...previousTaskDetail, status: 'archived'})
      }

      return {previousQueries, previousRepoQueries, previousTaskDetail, taskDetailKey}
    },
    onError: (_err, _vars, context) => {
      // Rollback to previous cache state
      if (context?.previousQueries) {
        for (const [key, data] of context.previousQueries) {
          queryClient.setQueryData(key, data)
        }
      }

      if (context?.previousRepoQueries) {
        for (const [key, data] of context.previousRepoQueries) {
          queryClient.setQueryData(key, data)
        }
      }

      if (context?.previousTaskDetail && context?.taskDetailKey) {
        queryClient.setQueryData(context.taskDetailKey, context.previousTaskDetail)
      }

      archiveDialog?.setArchiveStatus(ARCHIVE_STATUS.ERROR)
    },
    onSuccess: () => {
      archiveDialog?.setArchiveStatus(ARCHIVE_STATUS.IDLE)
    },
    onSettled: () => {
      // Invalidate to ensure consistency with server
      const taskQueryKey = agentTaskQueryKey(repoOwner, repoName, taskId)
      queryClient.invalidateQueries({queryKey: taskQueryKey})
      if (repoOwner && repoName) {
        queryClient.invalidateQueries({queryKey: ['repo-tasks', repoOwner, repoName]})
      }
      queryClient.invalidateQueries({queryKey: agentTasksQueryKey})
    },
  })
}
